import { MobileNav } from "@/app/mobile-nav";
import { Sidebar } from "@/app/sidebar";
import { CommandMenu } from "@/components/command-menu";
import { CreateIssueDialog } from "@/components/create-issue";
import { LiveCursors } from "@/components/live-cursors";
import { ShortcutsHelp } from "@/components/shortcuts-help";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { identifier } from "@/lib/format";
import { useHotkey } from "@/lib/hotkeys";
import { updateMany } from "@/lib/mutations";
import { presence } from "@/lib/presence";
import { recordVisit, type RecentKind } from "@/lib/recents";
import { useTable } from "@/lib/store";
import { sync } from "@/lib/sync";
import { openCommand, openCreateIssue, setMobileSidebar, setShortcutsOpen, targetIssueIds, toggleSidebar, useShortcutsOpen, useSidebar } from "@/lib/ui";
import { cn } from "@/lib/utils";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";

export function AppLayout() {
  const sidebar = useSidebar();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const help = useShortcutsOpen();

  // Tell others where we are (entity pages also set entityId).
  useEffect(() => {
    presence.set({ location: pathname, entityId: null, typing: false, cursor: null });
  }, [pathname]);

  useHotkey("c", () => openCreateIssue());
  useHotkey("mod+k", () => openCommand(), { inInputs: true });
  // "/" opens search outside fields; inside an editor it opens the slash menu instead.
  useHotkey("/", () => openCommand());
  useHotkey("mod+[", () => toggleSidebar());
  useHotkey("shift+?", () => setShortcutsOpen(true));
  useHotkey("g h", () => navigate({ to: "/" }));
  useHotkey("g i", () => navigate({ to: "/inbox" }));
  useHotkey("g m", () => navigate({ to: "/my-issues" }));
  useHotkey("g t", () => navigate({ to: "/stickies" }));
  useHotkey("g p", () => navigate({ to: "/projects" }));
  useHotkey("g r", () => navigate({ to: "/roadmap" }));
  useHotkey("g d", () => navigate({ to: "/ideas" }));
  useHotkey("g v", () => navigate({ to: "/views" }));
  useHotkey("g s", () => navigate({ to: "/settings/$section", params: { section: "profile" } }));
  const onIssues = (page: Parameters<typeof openCommand>[0]) => () => {
    const ids = targetIssueIds();
    if (ids.length) openCommand(page, ids);
  };
  useHotkey("s", onIssues("status"));
  useHotkey("p", onIssues("priority"));
  useHotkey("a", onIssues("assignee"));
  useHotkey("l", onIssues("labels"));
  useHotkey("shift+p", onIssues("project"));
  useHotkey("shift+c", onIssues("cycle"));
  useHotkey("e", onIssues("estimate"));
  useHotkey("shift+d", onIssues("dueDate"));
  useHotkey("i", () => {
    const ids = targetIssueIds();
    const me = sync.me;
    if (ids.length && me) updateMany("issue", ids, { assigneeId: me });
  });

  return (
    <div className="flex h-dvh overflow-hidden bg-sidebar">
      <aside className={cn("hidden shrink-0 transition-[width] duration-150 md:block", sidebar.open ? "w-60" : "w-0 overflow-hidden")}>
        <Sidebar />
      </aside>
      <Sheet open={sidebar.mobileOpen} onOpenChange={setMobileSidebar}>
        <SheetContent side="left" className="w-[82vw] max-w-80 border-r-0 bg-sidebar p-0 pt-[env(safe-area-inset-top)]">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <Sidebar />
        </SheetContent>
      </Sheet>
      {/* The content sits on an inset panel, raised off the app chrome. */}
      {/* On phones the floating tab bar takes the bottom of the screen. */}
      <div className={cn("min-w-0 flex-1 max-md:bg-background max-md:pb-[calc(80px+env(safe-area-inset-bottom))] md:py-2 md:pr-2", !sidebar.open && "md:pl-2")}>
        <main className="relative h-full overflow-hidden bg-background md:rounded-[10px] md:border md:border-border md:shadow-[0_1px_2px_rgb(0_0_0/0.04)] dark:md:shadow-none">
          <Outlet />
          <LiveCursors />
        </main>
      </div>
      <MobileNav />
      <CommandMenu />
      <CreateIssueDialog />
      <ShortcutsHelp open={help} onOpenChange={setShortcutsOpen} />
      <RecentsTracker pathname={pathname} />
    </div>
  );
}

/** Remembers which issue, project, document, … this address shows, for Home's Recents. */
function RecentsTracker({ pathname }: { pathname: string }) {
  const issues = useTable("issue");
  const teams = useTable("team");
  const cycles = useTable("cycle");
  useEffect(() => {
    const [, kind, a, b, c] = pathname.split("/");
    const direct: Partial<Record<string, RecentKind>> = { project: "project", initiative: "initiative", doc: "document", idea: "idea", view: "view", dashboard: "dashboard", customer: "customer" };
    if (kind === "issue" && a) {
      const found = Object.values(issues).find((i) => identifier(i, teams).toLowerCase() === a.toLowerCase()) ?? issues[a.toLowerCase()];
      if (found) recordVisit("issue", found.id);
    } else if (kind === "team" && b === "cycles" && c) {
      const team = Object.values(teams).find((t) => t.key === a?.toUpperCase());
      const cycle = team && Object.values(cycles).find((x) => x.teamId === team.id && String(x.number) === c);
      if (cycle) recordVisit("cycle", cycle.id);
    } else if (direct[kind] && a) {
      recordVisit(direct[kind]!, a);
    }
  }, [pathname, issues, teams, cycles]);
  return null;
}
